import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { updateAnalyticsPrefs } from "@/state/slices/meSlice";
import styles from "./UsageNotice.module.css";

/** Usage-data notice (features/usage_analytics.md). Analytics is on by
 *  default; this tells every user once per notice revision, and "Turn off"
 *  opts out on the spot. Someone who HAS opted out sees the second version
 *  below once a day (first visit of the UTC day — the server decides, via
 *  analytics.notice_due): a standing offer to turn it back on, never a gate.
 *  Dismissing settles the day; the opt-out itself only changes if they say
 *  so. The same switch lives in the help panel. Waits while a guided tour is
 *  on screen so the two don't stack. */
export function UsageNotice() {
  const dispatch = useAppDispatch();
  const seen = useAppSelector((s) => s.me.analyticsNoticeSeen);
  const optedOut = useAppSelector((s) => s.me.analyticsOptOut);
  const tourActive = useAppSelector((s) => s.tour.active !== null);

  if (seen || tourActive) return null;

  const privacy = (
    <a href="/privacy" target="_blank" rel="noopener noreferrer">
      Privacy Policy
    </a>
  );

  if (optedOut) {
    return (
      <div className={styles.card} role="dialog" aria-labelledby="usage-notice-title">
        <div id="usage-notice-title" className={styles.title}>
          Usage data is off
        </div>
        <p className={styles.body}>
          You've turned off sharing how you use Pulsarix, so we can't see which
          features and strategies are worth building on. Nothing is collected
          while it's off, it would stay on our own servers, and it is never
          shared with advertisers. {privacy}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={() => dispatch(updateAnalyticsPrefs({ notice_seen: true }))}
          >
            Keep it off
          </button>
          <button
            type="button"
            className={styles.primary}
            onClick={() => dispatch(updateAnalyticsPrefs({ opt_out: false, notice_seen: true }))}
          >
            Turn it on
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card} role="dialog" aria-labelledby="usage-notice-title">
      <div id="usage-notice-title" className={styles.title}>
        Help us improve Pulsarix
      </div>
      <p className={styles.body}>
        We record how you use the app (the features, tools, strategies and
        signal views you open) to make it better. It stays on our own servers
        and is never shared with advertisers. You can turn this off at any time
        in Help. {privacy}
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondary}
          onClick={() => dispatch(updateAnalyticsPrefs({ opt_out: true, notice_seen: true }))}
        >
          Turn off
        </button>
        <button
          type="button"
          className={styles.primary}
          onClick={() => dispatch(updateAnalyticsPrefs({ notice_seen: true }))}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
